import { useCallback, useEffect, useState } from "react";
import { Milestone, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, apiError, dateTime } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export function TimelinePanel({ caseId, canEdit = false }) {
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState({ title: "", description: "" });
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/timeline`).then(({ data }) => setEvents(data)).catch(() => {});
  }, [caseId]);

  useEffect(load, [load]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    try {
      await api.post(`/cases/${caseId}/timeline`, { ...form, type: "update" });
      toast.success("Timeline event added");
      setForm({ title: "", description: "" });
      setOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="timeline-panel">
      {canEdit ? (
        <div className="mb-6">
          {open ? (
            <form onSubmit={submit} className="glass rounded-xl p-5 space-y-3" data-testid="timeline-form">
              <Input
                data-testid="timeline-title-input"
                placeholder="Event title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                className="bg-white/5 border-white/15"
              />
              <Textarea
                data-testid="timeline-description-input"
                placeholder="Detail shared with the client"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                className="bg-white/5 border-white/15"
              />
              <div className="flex gap-2">
                <Button data-testid="timeline-save-btn" disabled={saving} className="rounded-full bg-gold text-navy hover:bg-gold-dark">
                  {saving ? "Saving…" : "Add event"}
                </Button>
                <Button type="button" variant="ghost" onClick={() => setOpen(false)} className="rounded-full text-[#9CA3AF] hover:bg-white/10">
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <Button
              data-testid="timeline-add-btn"
              onClick={() => setOpen(true)}
              variant="outline"
              className="rounded-full border-white/20 bg-transparent hover:bg-white/10 hover:border-white/30"
            >
              <Plus className="h-4 w-4 mr-2 text-gold" /> Add timeline event
            </Button>
          )}
        </div>
      ) : null}

      <ol className="relative border-l border-white/10 ml-3 space-y-8">
        {events.map((ev, i) => (
          <li key={ev.id} className="pl-8 animate-fade-up" style={{ animationDelay: `${i * 40}ms` }} data-testid="timeline-event">
            <span
              className={`absolute -left-[9px] flex h-[18px] w-[18px] items-center justify-center rounded-full border ${
                ev.type === "milestone" ? "border-gold bg-gold/20" : "border-white/25 bg-navy"
              }`}
            >
              {ev.type === "milestone" ? <Milestone className="h-2.5 w-2.5 text-gold" /> : null}
            </span>
            <p className="text-xs uppercase tracking-widest text-[#9CA3AF]">{dateTime(ev.occurred_at)}</p>
            <h4 className="text-base font-display text-[#F3F4F6] mt-1">{ev.title}</h4>
            {ev.description ? <p className="text-sm text-[#9CA3AF] mt-1 max-w-2xl">{ev.description}</p> : null}
            <p className="text-xs text-[#6B7280] mt-2">{ev.author}</p>
          </li>
        ))}
        {!events.length ? <li className="pl-8 text-sm text-[#9CA3AF]">No timeline events recorded yet.</li> : null}
      </ol>
    </div>
  );
}
